import {
  Award,
  Building,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Stethoscope,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { canChangeFirm, canManageFirmUsers } from "../../permissions/firms";
import { useCanReadSubscriptions, usePermissionsV2Loaded } from "../../permissions/v2";
import { SpecialtiesRepository, useRepository } from "../../repositories";
import { FirmContextDropdown } from "../FirmContext";

interface Specialty {
  id: string;
  name: string;
  templateId: string;
}

interface SpecialtyResponse {
  specialty_id: string;
  specialty_name: string;
  template_id: string;
}

export const FirmMenu = () => {
  const { firmContext, isMyCases } = useAuth();
  const isPermissionsLoaded = usePermissionsV2Loaded();
  const canViewBilling = useCanReadSubscriptions(firmContext?.firmId);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const specialtiesRepository = useRepository(SpecialtiesRepository) as any;

  const [showFirmMenu, setShowFirmMenu] = useState<boolean>(false);
  const [showSpecialtiesMenu, setShowSpecialtiesMenu] = useState<boolean>(false);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [specialtiesLoading, setSpecialtiesLoading] = useState<boolean>(false);

  const firmMenuRef = useRef<HTMLDivElement>(null);

  const fetchSpecialties = async () => {
    if (specialties.length > 0 || specialtiesLoading) return;

    try {
      setSpecialtiesLoading(true);
      const data = await specialtiesRepository.getFirmSpecialties();
      setSpecialties(
        data?.map((specialty: SpecialtyResponse) => ({
          id: specialty.specialty_id,
          name: specialty.specialty_name,
          templateId: specialty.template_id,
        })) || []
      );
    } catch (error) {
      console.error("Error fetching specialties:", error);
      setSpecialties([]);
    } finally {
      setSpecialtiesLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (firmMenuRef.current && !firmMenuRef.current.contains(event.target as Node)) {
        setShowFirmMenu(false);
      }
      if (
        event.target instanceof Element &&
        !event.target.closest(".specialties-menu-item") &&
        !event.target.closest(".specialties-submenu")
      ) {
        setShowSpecialtiesMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleFirmMenuClick = () => {
    setShowFirmMenu(!showFirmMenu);
    if (!showFirmMenu && canManageFirmUsers(firmContext) && !isMyCases) {
      fetchSpecialties();
    }
  };

  const handleSpecialtiesClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSpecialtiesMenu(!showSpecialtiesMenu);
  };

  if (!firmContext) return null;

  const showFirmSwitcher = canChangeFirm(firmContext);
  const showAdminSection = !isMyCases && canManageFirmUsers(firmContext);
  const showBillingSection =
    !isMyCases && isPermissionsLoaded && Boolean(firmContext.firmId && canViewBilling);

  return (
    <div className="firm-menu" ref={firmMenuRef}>
      <button
        className={`firm-menu-trigger ${showFirmMenu ? "active" : ""}`}
        onClick={handleFirmMenuClick}
      >
        <div className="firm-avatar">
          {isMyCases ? "M" : firmContext.firmName ? firmContext.firmName[0].toUpperCase() : "F"}
        </div>
        <span className="firm-name-trigger">
          {isMyCases ? "My cases (All Firms)" : firmContext.firmName}
        </span>
        <ChevronDown size={16} className={`trigger-chevron ${showFirmMenu ? "rotated" : ""}`} />
      </button>

      {showFirmMenu && (
        <div className="firm-menu-dropdown">
          <FirmContextDropdown />

          {showAdminSection && (
            <>
              {showFirmSwitcher && <div className="menu-divider" />}
              <div className="menu-section-label">ADMINISTRATION</div>
              <Link
                to="/physicians"
                className="user-menu-item"
                onClick={() => setShowFirmMenu(false)}
              >
                <Stethoscope size={18} />
                <span>Physicians</span>
              </Link>
              <Link
                to="/firm-settings"
                className="user-menu-item"
                onClick={() => setShowFirmMenu(false)}
              >
                <Building size={18} />
                <span>Firm Settings</span>
              </Link>

              <div
                className="user-menu-item specialties-menu-item"
                onClick={handleSpecialtiesClick}
              >
                <div className="flex w-full items-center gap-3">
                  <Award size={18} />
                  <span>Specialties</span>
                  <ChevronRight className="ml-auto h-4 w-4" />
                </div>
                {showSpecialtiesMenu && (
                  <div className="specialties-submenu">
                    {specialtiesLoading ? (
                      <div className="user-menu-item">Loading...</div>
                    ) : specialties.length > 0 ? (
                      specialties.map((specialty) => (
                        <Link
                          key={specialty.id}
                          to={`/templates/${specialty.templateId}`}
                          state={{
                            templateId: specialty.templateId,
                            type: "specialty",
                            specialtyId: specialty.id,
                            specialtyName: specialty.name,
                          }}
                          className="user-menu-item"
                          onClick={() => {
                            setShowFirmMenu(false);
                            setShowSpecialtiesMenu(false);
                          }}
                        >
                          {specialty.name}
                        </Link>
                      ))
                    ) : (
                      <div className="user-menu-item">No specialties found</div>
                    )}
                  </div>
                )}
              </div>

              <Link to="/users" className="user-menu-item" onClick={() => setShowFirmMenu(false)}>
                <Users size={18} />
                <span>Users</span>
              </Link>
            </>
          )}

          {showBillingSection && (
            <>
              {(showFirmSwitcher || showAdminSection) && <div className="menu-divider" />}
              <div className="menu-section-label">BILLING</div>
              <Link
                to="/subscription"
                className="user-menu-item"
                onClick={() => setShowFirmMenu(false)}
              >
                <DollarSign size={18} />
                <span>Subscriptions</span>
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
};
